import { Route, useLocation, Redirect } from 'react-router-dom';
import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { SignedIn, SignedOut, RedirectToSignIn, useAuth, ClerkFailed, ClerkLoaded, ClerkLoading } from '@clerk/clerk-react';
import { IsAuthenticated } from './services/authService';
import { useState, useEffect } from 'react';
import { logInfo } from './services/loggerService';

import { useIsMobile, useIsXL } from './hooks/useMediaQuery';
import { DesktopSidebar, DesktopHeader, MobileHeader, MobileSidebar } from './components/ui';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { InvitationProvider } from './contexts/InvitationContext';
import { RefreshProvider } from './contexts/RefreshContext';
import { ToastProvider } from './contexts/ToastContext';
import { UserCourseProvider } from './contexts/UserCourseContext';
import { NotificationsDropdown } from './components/ui/NotificationsDropdown';
import { useNotifications } from './contexts/NotificationsContext';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { PlansPage } from './pages/PlansPage';
import { BillingPage } from './pages/BillingPage';
import { MyTeamPage } from './pages/MyTeamPage';
import { MyProgressPage } from './pages/MyProgressPage';
import { CertificationCoursesPage } from './pages/CertificationCoursesPage';
import { AllCoursesPage } from './pages/AllCoursesPage';
import { ResourceLibraryPage } from './pages/ResourceLibraryPage';
import { InvitedPage } from './pages/InvitedPage';
import { CoursePage } from './pages/CoursePage';
import { GlobalErrorModal } from './components/ui/GlobalErrorModal';
import { GlobalBadgeModal } from './components/ui/GlobalBadgeModal';
import { AuthGuard } from './components/auth/AuthGuard';
import { ClerkUnavailablePage } from './components/auth/ClerkUnavailablePage';
import { GuidedTour } from './components/tour';
import Dashboard from './pages/Dashboard';
import HomePage from './pages/HomePage';
import MysteryShopPage from './pages/MysteryShopPage';
import MysteryShopCallPage from './pages/MysteryShopCallPage';


/* Global styles (includes theme variables) */
import './styles/global.scss';

setupIonicReact();

function AppLayout() {
  const location = useLocation();
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const isMobile = useIsMobile();
  const isXL = useIsXL();
  const { isSignedIn } = useAuth();
  const isHomePage = location.pathname === '/';
  const isInvited = location.pathname === '/invited';
  const isMysteryShopCall = location.pathname.startsWith('/mystery-shop-call');
  const isNonProd = import.meta.env.VITE_ENV !== 'prod';
  const {
    isOpen: isNotificationsOpen,
    notifications,
    unreadCount,
    dropdownPosition,
    handleNotificationClick,
    handleMarkAllRead,
    handleClearAll,
  } = useNotifications();

  // Automatically manage sidebar state based on screen size
  useEffect(() => {
    if (isXL) {
      setIsDesktopSidebarOpen(true); // Always open on XL screens
    } else {
      setIsDesktopSidebarOpen(false); // Default closed on smaller screens
    }
  }, [isXL]);

  // Render these pages separately without any layout
  if (isHomePage || isInvited || isMysteryShopCall) {
    return (
      <IonRouterOutlet id="main-content" animated={isMobile}>
        <Route exact path="/">
          <SignedOut>
            <HomePage />
          </SignedOut>
          <SignedIn>
            <Redirect to="/dashboard" />
          </SignedIn>
        </Route>
        <Route exact path="/invited">
          <InvitedPage />
        </Route>
        <Route exact path="/mystery-shop-call/:callId">
          <MysteryShopCallPage />
        </Route>
      </IonRouterOutlet>
    );
  }

  return (
    <GuidedTour enabled={isSignedIn} autoStartOnlyWhenPath="/dashboard">
    <>
      {/* Mobile Side Menu (Drawer) */}
      <MobileSidebar disabled={!isMobile} />

      {/* Desktop Sidebar (Fixed) */}
      {!isMobile && (
        <SignedIn>
          <DesktopSidebar isOpen={isDesktopSidebarOpen} />
        </SignedIn>
      )}

      {/* Main Layout Container */}
      <div className={`min-h-screen flex flex-col transition-all duration-300 ease-in-out ${isSignedIn && !isMobile && isDesktopSidebarOpen ? 'ml-64' : isSignedIn && !isMobile ? 'ml-20' : ''}`}>
        {/* Header */}
        <SignedIn>
          {isMobile ? (
            <MobileHeader />
          ) : (
            <DesktopHeader onToggleSidebar={() => setIsDesktopSidebarOpen(!isDesktopSidebarOpen)} isXL={isXL} />
          )}
        </SignedIn>

        {/* Page Content */}
        <div className="flex-1 relative">
          <IonRouterOutlet id="main-content" animated={isMobile}>
            <Route exact path="/dashboard">
              <SignedIn>
                <AuthGuard>
                  <Dashboard />
                </AuthGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </Route>
            <Route exact path="/sign-in">
              <SignInPage />
            </Route>
            <Route exact path="/sign-up">
              <SignUpPage />
            </Route>
            <Route exact path="/plans">
              <SignedIn>
                <AuthGuard>
                  <PlansPage />
                </AuthGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </Route>
            <Route exact path="/billing">
              <SignedIn>
                <AuthGuard>
                  <BillingPage />
                </AuthGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </Route>
            <Route exact path="/my-team">
              <SignedIn>
                <AuthGuard>
                  <MyTeamPage />
                </AuthGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </Route>
            <Route exact path="/my-progress">
              <SignedIn>
                <AuthGuard>
                  <MyProgressPage />
                </AuthGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </Route>
            <Route exact path="/badges">
              <Redirect to="/my-progress" />
            </Route>
            <Route exact path="/certification-courses">
              <SignedIn>
                <AuthGuard>
                  <CertificationCoursesPage />
                </AuthGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </Route>
            <Route exact path="/all-courses">
              <SignedIn>
                <AuthGuard>
                  <AllCoursesPage />
                </AuthGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </Route>
            <Route exact path="/resource-library">
              {isNonProd ? (
                <>
                  <SignedIn>
                    <AuthGuard>
                      <ResourceLibraryPage />
                    </AuthGuard>
                  </SignedIn>
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                </>
              ) : (
                <Redirect to="/dashboard" />
              )}
            </Route>
            <Route exact path="/mystery-shop">
              {/* {isNonProd ? ( */}  {/* Commented out to enable in all environments */}
              <>
                <SignedIn>
                  <AuthGuard>
                    <MysteryShopPage />
                  </AuthGuard>
                </SignedIn>
                <SignedOut>
                  <RedirectToSignIn />
                </SignedOut>
              </>
              {/* ) : (
                <Redirect to="/dashboard" />
              )} */}
            </Route>
            <Route path="/course/:slug">
              <SignedIn>
                <AuthGuard>
                  <CoursePage />
                </AuthGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </Route>
            <Route exact path="/home">
              <Redirect to="/" />
            </Route>
          </IonRouterOutlet>
        </div>
      </div>

      {/* Notifications Dropdown - rendered at app level for visibility on mobile */}
      <NotificationsDropdown
        isOpen={isNotificationsOpen}
        notifications={notifications}
        unreadCount={unreadCount}
        position={dropdownPosition}
        onNotificationClick={handleNotificationClick}
        onMarkAllRead={handleMarkAllRead}
        onClearAll={handleClearAll}
      />
    </>
    </GuidedTour>
  );
}

function AppContent() {
  // Log version once on app load
  useEffect(() => {
    const version = import.meta.env.VITE_VERSION;
    logInfo(`App loaded - Version: ${version}`);
  }, []);

  return (
    <IonApp>
      <IonReactRouter>
        <UserCourseProvider>
          <AppLayout />
          {/* Global Error Modal - catches unhandled API errors */}
          <GlobalErrorModal />
          {/* Global Badge Modal - shows badge earned celebrations */}
          <GlobalBadgeModal />
        </UserCourseProvider>
      </IonReactRouter>
    </IonApp>
  );
}

function ClerkLoadingWithTimeout() {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), 10_000);
    return () => clearTimeout(timer);
  }, []);

  if (timedOut) {
    return <ClerkUnavailablePage />;
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
    </div>
  );
}

const App: React.FC = () => {
  return (
    <>
      <ClerkFailed>
        <ClerkUnavailablePage />
      </ClerkFailed>
      <ClerkLoading>
        <ClerkLoadingWithTimeout />
      </ClerkLoading>
      <ClerkLoaded>
        <ToastProvider>
          <NotificationsProvider>
            <InvitationProvider>
              <RefreshProvider>
                <IsAuthenticated />
                <AppContent />
              </RefreshProvider>
            </InvitationProvider>
          </NotificationsProvider>
        </ToastProvider>
      </ClerkLoaded>
    </>
  );
};

export default App;
